import { fetchFeaturedCompanies, FeaturedCompany } from '../oc2api/featured_companies';

/**
 * ワンキャリ注目企業をonecareer2nd APIから取得して表示する
 *
 * ## 実装経緯
 * onecareer と onecareer2nd で注目企業の表示ロジックが異なり、
 * 表示される企業が一致しない問題があった。
 *
 * - onecareer2nd: 契約の卒業年度フィルタリングなし（正しい仕様）
 * - onecareer: 契約の卒業年度フィルタリングあり（誤った実装）
 *
 * onecareer2nd の実装が正しい仕様であるため、onecareer リポジトリで管理されている
 * 全ページ（トップページ、マイページ等）からも onecareer2nd の
 * `/api/v1/featured_companies` APIを直接呼び出すように変更。
 *
 * これにより、Single Source of Truth（唯一の真実の情報源）を実現し、
 * どのページから遷移しても一貫した注目企業が表示される。
 *
 * ## 動作フロー
 * 1. ページ読み込み時（DOMContentLoaded）に初期化
 * 2. モーダル開閉時にも初期化を試みる（DOMが後から追加される場合に備えて）
 * 3. 「ワンキャリ注目企業」タブクリック時にAPI呼び出し
 * 4. レスポンスをHTMLにレンダリング
 * 5. 重複呼び出しを防ぐため、`loaded` フラグで制御（1度のみ実行）
 *
 * ## イベント処理
 * - `shown.bs.tab`: Bootstrap タブ切り替え完了イベント（メイン）
 * - `click`: フォールバック（shown.bs.tab が発火しない環境向け）
 * - どちらが先に発火しても、`loaded` フラグにより1度だけ実行される
 *
 * ## セキュリティ
 * - CSRF トークン: oc2api.ts で自動的に `X-CSRF: 'onecareer'` を付与
 * - XSS対策: escapeHtml() でユーザー入力をエスケープ
 *
 * @see onecareer/app/javascript/oc2api/featured_companies.ts - API呼び出し層
 * @see onecareer2nd/backend/app/controllers/api/v1/featured_companies_controller.rb - APIエンドポイント
 */
class FeaturedCompaniesLoader {
  private listElement: HTMLElement | null;
  private loadingElement: HTMLElement | null;

  constructor() {
    this.listElement = document.getElementById('js-featured-companies-list');
    this.loadingElement = document.getElementById('js-featured-companies-loading');
  }

  /**
   * 注目企業を読み込む
   */
  async load(): Promise<void> {
    if (!this.listElement) return;

    try {
      const data = await fetchFeaturedCompanies();
      this.render(data.featured_companies);
    } catch (error) {
      console.error('[FeaturedCompaniesLoader] Error:', error);
      this.renderError();
    } finally {
      this.hideLoading();
    }
  }

  /**
   * 注目企業リストをレンダリング
   */
  private render(companies: FeaturedCompany[]): void {
    if (!this.listElement) return;

    if (!companies || companies.length === 0) {
      this.renderEmpty();
      return;
    }

    this.listElement.innerHTML = companies.map((company) => this.createCompanyItem(company)).join('');
  }

  /**
   * 企業アイテムのHTMLを生成
   */
  private createCompanyItem(company: FeaturedCompany): string {
    const logoHtml = company.square_logo
      ? `<img src="${this.escapeHtml(company.square_logo)}"
             alt="${this.escapeHtml(company.company_name)}"
             class="v2-sp-global-search-modal-pickup-company__logo" />`
      : `<div class="v2-no-image-logo v2-sp-global-search-modal-pickup-company__logo">NO IMAGE</div>`;

    return `
      <li class="v2-sp-global-search-modal-pickup-company">
        <a href="/companies/${company.id}" class="v2-sp-global-search-modal-pickup-company__link">
          ${logoHtml}
          <div class="v2-sp-global-search-modal-pickup-company__right">
            <div class="v2-sp-global-search-modal-pickup-company__right-top">
              <div class="v2-sp-global-search-modal-pickup-company__business-categories">
                <span class="v2-sp-global-search-modal-pickup-company__business-categories-item">
                  ${this.escapeHtml(company.business_category_name)}
                </span>
                <span class="v2-sp-global-search-modal-pickup-company__business-categories-item">
                  ${this.escapeHtml(company.business_subcategory_name)}
                </span>
              </div>
            </div>
            <div class="v2-sp-global-search-modal-pickup-company__name-wrapper">
              <span class="v2-sp-global-search-modal-pickup-company__name">
                ${this.escapeHtml(company.company_name)}
              </span>
              <div class="v2-sp-global-search-modal-pickup-company__official">
                公式
              </div>
            </div>
          </div>
        </a>
      </li>
    `;
  }

  /**
   * エラー表示
   */
  private renderError(): void {
    if (!this.listElement) return;

    this.listElement.innerHTML = `
      <li style="text-align: center; padding: 20px; color: #999;">
        注目企業の読み込みに失敗しました
      </li>
    `;
  }

  /**
   * 空の場合の表示
   */
  private renderEmpty(): void {
    if (!this.listElement) return;

    this.listElement.innerHTML = `
      <li style="text-align: center; padding: 20px; color: #999;">
        注目企業はありません
      </li>
    `;
  }

  /**
   * ローディング表示を非表示
   */
  private hideLoading(): void {
    if (this.loadingElement) {
      this.loadingElement.style.display = 'none';
    }
  }

  /**
   * HTMLエスケープ
   */
  private escapeHtml(text: string): string {
    const map: { [key: string]: string } = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    };
    return String(text).replace(/[&<>"']/g, (m) => map[m]);
  }
}

// モーダルとタブの初期化（一度だけ実行）
let initialized = false;
let loaded = false;

function initFeaturedCompaniesLoader(): void {
  if (initialized) return;

  const pickupTab = document.querySelector('a[href="#sp-modal-search-tab-pickup"]');
  if (!pickupTab) return;

  initialized = true;
  const loader = new FeaturedCompaniesLoader();

  // shown.bs.tab イベント（Bootstrap タブ切り替え完了時）
  pickupTab.addEventListener('shown.bs.tab', () => {
    if (!loaded) {
      loader.load();
      loaded = true;
    }
  });

  // 念のため click イベントも追加（shown.bs.tab が発火しない場合のフォールバック）
  pickupTab.addEventListener('click', () => {
    // タブがアクティブになるまで少し待つ
    setTimeout(() => {
      if (!loaded) {
        loader.load();
        loaded = true;
      }
    }, 200);
  });
}

// DOMContentLoaded時に初期化
document.addEventListener('DOMContentLoaded', () => {
  initFeaturedCompaniesLoader();

  // 検索モーダルを開くボタンにのみイベントリスナーを設定
  // グローバルなclickイベントではなく、特定の要素に限定することで他のモーダルへの影響を防ぐ
  const searchDialogOpenButtons = document.querySelectorAll('.js-scroll-dialog-open');
  searchDialogOpenButtons.forEach((button) => {
    button.addEventListener('click', () => {
      setTimeout(() => {
        initFeaturedCompaniesLoader();
      }, 100);
    });
  });
});
